/**
 * "What does this session run on the latest release?" — one answer, two places.
 *
 * The navbar badge and Settings › Access read the SAME value: a badge that says
 * "latest release" while the settings page (or the requests) disagree is worse
 * than no badge, and the two surfaces drifted apart the moment each did its own
 * token read.
 *
 * Routing comes from `getRoutingToken()` — the live bearer, not the page-load
 * claims — because that is what the authorizer decides on. `null` means nothing
 * routes to the latest release, and every caller renders nothing for it: the
 * live release is the default and is never labelled (see `summariseBlueRouting`).
 */
import * as React from "react";
import { routingForToken } from "@authe/fe-commons/api";
import { useSession } from "@authe/ui-core/session";
import { summariseBlueRouting } from "@authe/fe-commons/blue-green";
import { readQaOverride } from "@authe/fe-commons/session";
import { appEnv } from "@/config";
import { getRoutingToken } from "@/app/routing-token";

export interface BlueRouting {
  /** What runs on the latest release ("All services", "a, b"), or `null` for none. */
  summary: string | null;
  /** The `?qa=` value holding this session there, if it came from an override. */
  override: string | null;
}

export function useBlueRouting(): BlueRouting {
  const { session } = useSession();
  // Keyed on the session: the bearer is re-minted through it (sign-in, silent
  // refresh, "Re-read my routing"), which is the only thing that can change the
  // answer — `getRoutingToken()` itself is a module singleton, not state.
  return React.useMemo(() => {
    const token = getRoutingToken();
    return {
      summary: token ? summariseBlueRouting(routingForToken(token, appEnv)) : null,
      override: readQaOverride(),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);
}
